import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { clearAllPackCaches } from '../lib/packCache';
import type { Profile } from '../lib/types';
import { AuthContext, type SignUpResult } from './useAuth';

// Component-only export so Vite Fast Refresh stays happy. The hook
// (useAuth) and the context object live in ./useAuth.

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  async function fetchProfile(userId: string) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    setProfile(data);
  }

  async function refreshProfile() {
    if (user) {
      await fetchProfile(user.id);
    }
  }

  // Tracks the currently-loaded user id. Supabase re-emits an auth event
  // (TOKEN_REFRESHED, or a repeat SIGNED_IN) every time the tab regains focus.
  // Without this guard each refocus would call setUser() with a fresh User
  // object, changing its identity and re-running every page effect keyed on
  // [user] — which re-fetches and re-hydrates in-progress forms, wiping the
  // user's unsaved input (notably the purchase new/edit pages). We only react
  // when the user genuinely changes (sign in/out/switch) or Supabase reports an
  // explicit USER_UPDATED.
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: { session: s } } = await supabase.auth.getSession();
      setSession(s);
      setUser(s?.user ?? null);
      lastUserIdRef.current = s?.user?.id ?? null;
      if (s?.user) {
        await fetchProfile(s.user.id);
      }
      setLoading(false);
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // Keep the session fresh (the access token rotates on refresh), but only
      // touch user/profile state on a real identity change — see lastUserIdRef
      // above. This is what stops a tab refocus from clearing half-filled forms.
      setSession(s);
      const nextUser = s?.user ?? null;
      const nextId = nextUser?.id ?? null;
      if (nextId === lastUserIdRef.current && event !== 'USER_UPDATED') return;
      lastUserIdRef.current = nextId;
      setUser(nextUser);
      if (nextUser) {
        fetchProfile(nextUser.id).catch(() => { /* ignore — profile load is best-effort */ });
      } else {
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function signUp(email: string, password: string, displayName: string): Promise<SignUpResult> {
    // emailRedirectTo lands the user back at the dashboard after they
    // click the confirmation link in their inbox. The auth state change
    // picks up SIGNED_IN and the ProtectedRoute lets them through.
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    // When email confirmation is enabled in the Supabase dashboard,
    // signUp returns a user record but no active session. Surface that
    // so the Register page can show "Check your email" instead of
    // trying to navigate to /.
    const needsConfirmation = !error && !!data?.user && !data?.session;
    return { error: error as Error | null, needsConfirmation };
  }

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  }

  async function signOut() {
    await supabase.auth.signOut();
    setProfile(null);
    // M85 Phase b (D33) — sign-out cleared nothing local before this. That was
    // survivable while the only per-session cache stored integers keyed by
    // uuid (tripTaps); the packing cache stores firearm names, build labels
    // and ammo lots, which have no business outliving the session on a shared
    // or lost device.
    clearAllPackCaches();
  }

  // M29 — password recovery. Supabase emails a one-time link to the
  // address; clicking it lands the user on /reset-password with a
  // recovery token that Supabase consumes to mint a recovery session.
  async function resetPasswordForEmail(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  }

  async function updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error as Error | null };
  }

  async function deleteAccount() {
    const { error } = await supabase.rpc('delete_my_account');
    if (!error) {
      // The auth row is gone, but Supabase client still has a local
      // session cached. Clean it up so the user lands on /login and not
      // a half-authenticated state.
      await supabase.auth.signOut();
      setProfile(null);
    }
    return { error: error as Error | null };
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        resetPasswordForEmail,
        updatePassword,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
